import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "@fortawesome/fontawesome-free/css/all.min.css";

const API_URL = "https://wip.technicalcreatives.lk/api/tasks.php";

const columnsMap = {
  past: "Last Week",
  current: "This Week",
  later: "Later",
};

export default function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [taskInputs, setTaskInputs] = useState([""]);
  const [formStatus, setFormStatus] = useState("current");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [currentGroupId, setCurrentGroupId] = useState(null);

  const token = localStorage.getItem("token");

  const SidebarLink = ({ icon, text, href, onClick }) => (
    <a
      href={href || "#"}
      onClick={onClick}
      className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-blue-500 transition"
    >
      <i className={`fa-solid ${icon} w-5`}></i>
      <span className="text-sm font-medium">{text}</span>
    </a>
  );

  // ---------------- FETCH TASKS ----------------
  const fetchTasks = async () => {
  if (!token) return logout();
  setLoading(true);
  try {
    const res = await axios.post(`${API_URL}?action=all`, { token });
    if (res.data.success) {
      // Convert completed to boolean (0 → false, 1 → true)
      const tasksWithBool = res.data.tasks.map(t => ({
        ...t,
        completed: Number(t.completed) === 1,
      }));

      setTasks(tasksWithBool);
    } else {
      if (res.data.error === "Unauthorized")
        alert("Session expired. Please login again.");
      else console.warn(res.data);
    }
  } catch (err) {
    console.error(err);
    alert("Error loading tasks. Please login again.");
  } finally {
    setLoading(false);
  }
};

  // ---------------- WEEKLY UPDATE ----------------
// const checkWeeklyUpdate = async () => {
//   const lastRun = localStorage.getItem("weeklyTaskUpdate");
//   const today = new Date();
//   const todayStr = today.toDateString();

//   // Only run if it's Monday and not already updated today
//   if (today.getDay() === 1 && lastRun !== todayStr) {
//     try {
//       await axios.post(`${API_URL}?action=weekly-update`, { token });
//       localStorage.setItem("weeklyTaskUpdate", todayStr);
//       fetchTasks();
//     } catch (err) {
//       console.error("Weekly update failed:", err);
//     }
//   }
// };

  useEffect(() => {
    fetchTasks();
    // checkWeeklyUpdate();
  }, []);

  // ---------------- GROUP TASKS ----------------
  const groupByGroupId = (taskArray) => {
    const map = {};
    taskArray.forEach((task) => {
      const gid = `${task.group_id || task.id}-${task.user_id}`;
      // const gid = task.group_id || task.id;
      if (!map[gid]) map[gid] = [];
      map[gid].push(task);
    });
    return Object.values(map);
  };

  const groupedTasks = {
    past: groupByGroupId(tasks.filter((t) => t.status === "past")),
    current: groupByGroupId(tasks.filter((t) => t.status === "current")),
    later: groupByGroupId(tasks.filter((t) => t.status === "later")),
  };

  const userHasCardInStatus = (statusKey) => {
    return groupedTasks[statusKey].some(
      (group) => group[0]?.user_id === user.id
    );
  };

  // ---------------- DRAG & DROP ----------------
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const task = tasks.find((t) => t.id === parseInt(result.draggableId));
    if (!task) return;

    try {
      await axios.post(`${API_URL}?action=update&id=${task.id}`, {
        status: destination.droppableId,
        token,
      });
      fetchTasks();
    } catch {
      alert("Failed to update task.");
    }
  };

  // ---------------- CURRENT WEEK RANGE ----------------
  const getCurrentWeekRange = () => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((day + 6) % 7));
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    const format = (date) =>
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    return `${format(monday)} - ${format(friday)}`;
  };

 // ---------------- RENDER TASKS ----------------
  const renderGroupedCards = (taskGroups, status) => {

    return taskGroups.map((group, groupIndex) => (
      <div
        key={`group-${groupIndex}`}
        className="bg-white p-4 rounded-3xl shadow-sm border border-gray-100"
      >
        <ul className="mb-2">
          {group.map((task, taskIndex) => (
            <Draggable
              key={task.id}
              draggableId={task.id.toString()}
              index={taskIndex}
            >
              {(provided, snapshot) => (
                <li
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className={`text-sm font-medium flex items-center justify-between space-x-2 mb-2 p-2 rounded-md bg-white ${
                    snapshot.isDragging ? "ring-2 ring-blue-400" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded-full accent-green-700"
                      checked={task.completed} 
                      disabled={task.user_id !== user.id} 
                      onChange={async () => {
                        const newStatus = !task.completed;
                        setTasks(prev =>
                          prev.map(t => t.id === task.id ? { ...t, completed: newStatus } : t)
                        );
                        try {
                          await axios.post(`${API_URL}?action=update&id=${task.id}`, {
                            completed: newStatus,
                            token,
                          });
                        } catch {
                          alert("Failed to update task.");
                          // revert if failed
                          setTasks(prev =>
                            prev.map(t => t.id === task.id ? { ...t, completed: task.completed } : t)
                          );
                        }
                      }}
                    />
                    {editingTaskId === task.id ? (
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="border px-2 py-1 rounded w-full text-sm"
                      />
                    ) : (
                      <label
                        htmlFor={task.id}
                        className={`flex-1 ${
                          task.completed ? "text-green-700" : ""
                        }`}
                      >
                        {task.title}
                      </label>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {editingTaskId === task.id ? (
                      <>
                        <button
                          onClick={async () => {
                            if (!editedTitle.trim())
                              return alert("Task cannot be empty");
                            try {
                              await axios.post(
                                `${API_URL}?action=update&id=${task.id}`,
                                {
                                  title: editedTitle,
                                  token,
                                }
                              );
                              setEditingTaskId(null);
                              setEditedTitle("");
                              fetchTasks();
                            } catch {
                              alert("Failed to update task.");
                            }
                          }}
                          className="text-green-600 text-sm"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          onClick={() => {
                            setEditingTaskId(null);
                            setEditedTitle("");
                          }}
                          className="text-red-600 text-sm"
                        >
                          <i className="fas fa-close"></i>
                        </button>
                      </>
                    ) : task.user_id === user.id ? (
                      <>
                        <button
                          onClick={() => {
                            setEditingTaskId(task.id);
                            setEditedTitle(task.title);
                          }}
                          className="text-gray-500 text-sm"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={async () => {
                            if (
                              !window.confirm(
                                "Are you sure you want to delete this task?"
                              )
                            )
                              return;
                            try {
                              await axios.post(
                                `${API_URL}?action=delete&id=${task.id}`,
                                { token }
                              );
                              fetchTasks();
                            } catch {
                              alert("Failed to delete task.");
                            }
                          }}
                          className="text-red-600 text-xs"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </>
                    ) : null}
                  </div>
                </li>
              )}
            </Draggable>
          ))}
        </ul>

        {/* Group metadata */}
        <div className="relative text-xs text-gray-400 mt-2">
          {/* Left side: Avatar, Name, Date */}
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-200 text-xs uppercase font-bold text-blue-800 mt-1">
              {group[0]?.firstName?.[0] || "?"}
            </div>
            <div className="flex flex-col">
              <span>
                {group[0]?.firstName
                  ? `${group[0].firstName} ${
                      group[0].lastName || ""
                    }`
                  : "Unknown"}
              </span>
              <span>
                {group[0]?.created_at
                  ? new Date(group[0].created_at).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
          </div>

          {/* Right side: Add Task */}
          {group[0]?.user_id === user.id && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <button
                onClick={() => {
                  setFormStatus(status);
                  setCurrentGroupId(group[0]?.group_id || group[0]?.id);
                  setTaskInputs([""]);
                  setShowForm(true);
                }}
                className="text-blue-600 text-xl hover:text-blue-800"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    ));
  };

  // ---------------- MAIN RENDER ----------------
  return (
    <div className="flex flex-col min-h-screen">
      {/* Task Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-lg">
            <h2 className="text-xl font-medium mb-4">Add Your Tasks</h2>
            {taskInputs.map((value, index) => (
              <input
                key={index}
                type="text"
                value={value}
                onChange={(e) => {
                  const newInputs = [...taskInputs];
                  newInputs[index] = e.target.value;
                  setTaskInputs(newInputs);
                }}
                placeholder={`Task ${index + 1}`}
                className="w-full border p-2 rounded mb-2"
              />
            ))}
            <div className="flex justify-between mt-2 mb-4">
              <button
                onClick={() => setTaskInputs([...taskInputs, ""])}
                className="text-sm text-blue-600 underline"
              >
                + Add another
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="text-sm text-red-600 underline"
              >
                Cancel
              </button>
            </div>
            <button
              onClick={async () => {
                const validTitles = taskInputs.filter((t) => t.trim() !== "");
                if (!validTitles.length) return alert("Enter at least one task");
                try {
                  await axios.post(`${API_URL}?action=multi`, {
                    titles: validTitles,
                    status: formStatus,
                    user_id: user.id,
                    group_id: user.group_id,
                    token,
                  });
                  fetchTasks();
                  setShowForm(false);
                } catch {
                  alert("Failed to add tasks.");
                }
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded w-full"
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1">
        {/* Sidebar */}
        <div className="w-64 bg-gradient-to-b from-blue-600 to-blue-400 text-white py-6 px-4 flex flex-col">
          <img
            src="/images/TC-logo.png"
            alt="Logo"
            className="h-16 mb-8 mx-auto"
          />
          <nav className="flex flex-col gap-2">
            <SidebarLink icon="fa-house" text="Dashboard" href="/dashboard" />
            <SidebarLink icon="fa-list-check" text="My Tasks" href="/user-task" />
            <SidebarLink
              icon="fa-right-from-bracket"
              text="Logout"
              onClick={logout}
            />
          </nav>
        </div>

        {/* Board */}
        <main className="flex-1 overflow-auto">
          <header className="bg-white shadow-sm border-b sticky top-0 mb-10">
            <div className="max-w-screen-l mx-auto px-10 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-600 text-white h-9 w-9 rounded-lg flex items-center justify-center text-lg font-bold shadow">
                  W
                </div>
                <h1 className="text-2xl font-semibold text-gray-800">
                  WIP Board
                </h1>
              </div>
              <div className="hidden md:flex flex-col text-center">
                <span className="text-xs text-gray-400 uppercase tracking-wider">
                  Current Week
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {getCurrentWeekRange()}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex flex-col items-end">
                  <p className="text-sm font-medium text-gray-700">
                    Hello, {user?.firstName || "Guest"}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={logout}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Logout
                    </button>
                  </div>
                </div>
                <div className="bg-gray-300 h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold text-gray-800 uppercase shadow-sm">
                  {user?.firstName?.[0] || "G"}
                </div>
              </div>
            </div>
          </header>

          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-6 px-10 pb-10">
              {Object.entries(columnsMap).map(([statusKey, statusLabel], colIndex) => {
                const headerBg = [
                  "bg-purple-500",
                  "bg-orange-500",
                  "bg-yellow-500",
                ][colIndex];
                return (
                  <Droppable droppableId={statusKey} key={statusKey}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex-1 flex flex-col min-w-[300px]"
                      >
                        {/* Column Header */}
                        <div
                          className={`flex justify-between items-center mb-4 px-4 py-3 rounded-full text-white ${headerBg}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-white text-black px-2 py-1 rounded-full">
                              {tasks.filter((t) => t.status === statusKey).length}
                            </span>
                            <h3 className="font-semibold">{statusLabel}</h3>
                          </div>

                          {/* Add Button */}
                          {statusKey !== "past" && (
                            <button
                              onClick={() => {
                                if (
                                  (statusKey === "current" ||
                                    statusKey === "later") &&
                                  userHasCardInStatus(statusKey)
                                ) {
                                  alert(
                                    `You already have a card in "${columnsMap[statusKey]}".`
                                  );
                                  return;
                                }
                                setFormStatus(statusKey);
                                setTaskInputs([""]);
                                setShowForm(true);
                              }}
                              className="text-white text-center text-3xl leading-5"
                            >
                              +
                            </button>
                          )}
                        </div>

                        {/* Task Cards */}
                        <div className="space-y-4 mt-2">
                          {renderGroupedCards(groupedTasks[statusKey], statusKey)}
                        </div>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        </main>
      </div>
    </div>
  );
}
